import React, { useEffect, useState } from "react";
import { Plus, X, Trash2, Edit2, Save } from "lucide-react";
import { api } from "../../lib/api";

const blank = {
  slug: "",
  label_en: "",
  label_pt: "",
  color: "yellow",
  sort_order: 0,
};

const COLOR_OPTIONS = [
  { v: "yellow", l: "YELLOW", chip: "bg-kult-yellow text-black" },
  { v: "pink", l: "PINK", chip: "bg-kult-pink text-black" },
  { v: "white", l: "WHITE", chip: "bg-white text-black" },
  { v: "black", l: "BLACK", chip: "bg-black text-white border border-white" },
];

export const AdminTagsTab = () => {
  const [items, setItems] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(blank);
  const [editingSlug, setEditingSlug] = useState(null);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/tags");
    setItems(data || []);
  };
  useEffect(() => { load().catch(() => {}); }, []);

  const reset = () => { setForm(blank); setEditingSlug(null); setShowCreate(false); setError(""); };

  const startEdit = (t) => {
    setEditingSlug(t.slug);
    setForm({
      slug: t.slug,
      label_en: t.label_en || "",
      label_pt: t.label_pt || "",
      color: t.color || "yellow",
      sort_order: t.sort_order ?? 0,
    });
    setShowCreate(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setSubmitting(true);
    try {
      const payload = {
        slug: form.slug,
        label_en: form.label_en,
        label_pt: form.label_pt || form.label_en,
        color: form.color || "yellow",
        sort_order: Number(form.sort_order) || 0,
      };
      if (editingSlug) {
        await api.patch(`/admin/tags/${editingSlug}`, payload);
      } else {
        await api.post("/admin/tags", payload);
      }
      await load();
      reset();
    } catch (err) {
      setError(err?.response?.data?.detail || "Save failed");
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete tag "${t.label_en}"? It will be removed from any product that uses it.`)) return;
    try {
      await api.delete(`/admin/tags/${t.slug}`);
      await load();
    } catch (err) {
      window.alert(err?.response?.data?.detail || "Delete failed");
    }
  };

  const swatch = (color) => COLOR_OPTIONS.find((c) => c.v === color)?.chip || COLOR_OPTIONS[0].chip;

  return (
    <section data-testid="admin-tags-section">
      <div className="flex items-center justify-between mb-6">
        <p className="font-mono text-xs tracking-[0.3em] text-kult-yellow">/ PRODUCT TAGS</p>
        <button
          onClick={() => (showCreate ? reset() : setShowCreate(true))}
          className="bg-kult-pink text-black font-display text-base px-4 py-2 hover:bg-kult-yellow inline-flex items-center gap-2"
          data-testid="tags-toggle-create"
        >
          {showCreate ? <X size={16} /> : <Plus size={16} />} {showCreate ? "CLOSE" : "NEW TAG"}
        </button>
      </div>

      <p className="text-kult-muted text-sm mb-6">
        Create labels (e.g. <em>NEW</em>, <em>ICONIC</em>, <em>BESTSELLER</em>) that you can attach to products. Up to 3 visible tags per card.
      </p>

      {showCreate && (
        <form
          onSubmit={submit}
          className="border-2 border-kult-pink p-6 bg-kult-black2 mb-6 grid grid-cols-1 md:grid-cols-2 gap-4"
          data-testid="admin-tag-form"
        >
          <h3 className="md:col-span-2 font-display text-2xl">
            {editingSlug ? `EDIT · ${editingSlug}` : "NEW TAG"}
          </h3>
          <Field label="SLUG (lowercase, e.g. new)" required value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} testId="tag-slug" />
          <Field label="SORT ORDER" type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} testId="tag-sort" />
          <Field label="LABEL (EN)" required value={form.label_en} onChange={(e) => setForm({ ...form, label_en: e.target.value })} testId="tag-label-en" />
          <Field label="LABEL (PT)" value={form.label_pt} onChange={(e) => setForm({ ...form, label_pt: e.target.value })} testId="tag-label-pt" />
          <div className="md:col-span-2">
            <span className="font-mono text-[10px] tracking-[0.3em] text-kult-muted block mb-2">COLOR</span>
            <div className="flex flex-wrap gap-3" data-testid="tag-color-options">
              {COLOR_OPTIONS.map((c) => (
                <button
                  type="button"
                  key={c.v}
                  onClick={() => setForm({ ...form, color: c.v })}
                  className={`px-3 py-2 font-mono text-[10px] tracking-[0.3em] border-2 transition-all ${c.chip} ${form.color === c.v ? "border-kult-pink ring-2 ring-kult-pink" : "border-transparent"}`}
                  data-testid={`tag-color-${c.v}`}
                >
                  {c.l}
                </button>
              ))}
            </div>
          </div>
          {error && <p className="md:col-span-2 font-mono text-xs text-kult-pink">{error}</p>}
          <button
            type="submit"
            disabled={submitting}
            className="md:col-span-2 bg-kult-pink text-black font-display text-xl py-3 hover:bg-kult-yellow disabled:opacity-50 inline-flex items-center justify-center gap-2"
            data-testid="tag-submit"
          >
            <Save size={18} /> {submitting ? "SAVING..." : editingSlug ? "SAVE" : "CREATE"}
          </button>
        </form>
      )}

      <div className="border border-kult-border" data-testid="tags-list">
        <div className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-kult-border bg-kult-black2 font-mono text-[10px] tracking-[0.3em] text-kult-muted">
          <span className="col-span-1">#</span>
          <span className="col-span-2">SLUG</span>
          <span className="col-span-3">LABEL (EN)</span>
          <span className="col-span-2">LABEL (PT)</span>
          <span className="col-span-2">PREVIEW</span>
          <span className="col-span-2 text-right">ACTIONS</span>
        </div>
        {items.length === 0 && (
          <p className="px-4 py-6 font-mono text-xs text-kult-muted">No tags yet.</p>
        )}
        {items.map((t) => (
          <div key={t.slug} className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-kult-border items-center" data-testid={`tag-row-${t.slug}`}>
            <span className="col-span-1 font-mono text-xs text-kult-muted">{t.sort_order ?? 0}</span>
            <span className="col-span-2 font-mono text-xs text-white">{t.slug}</span>
            <span className="col-span-3 font-display text-base">{t.label_en}</span>
            <span className="col-span-2 font-mono text-xs text-kult-muted">{t.label_pt}</span>
            <span className="col-span-2">
              <span className={`inline-block font-mono text-[10px] tracking-[0.25em] px-2 py-1 ${swatch(t.color)}`}>
                {t.label_en}
              </span>
            </span>
            <div className="col-span-2 flex items-center justify-end gap-2">
              <button onClick={() => startEdit(t)} className="p-1.5 border border-kult-border hover:border-kult-pink" data-testid={`tag-edit-${t.slug}`} title="Edit"><Edit2 size={14} /></button>
              <button onClick={() => remove(t)} className="p-1.5 border border-kult-border hover:border-kult-pink text-kult-pink" data-testid={`tag-delete-${t.slug}`} title="Delete"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

function Field({ label, testId, className = "", ...props }) {
  return (
    <label className={`block ${className}`}>
      <span className="font-mono text-[10px] tracking-[0.3em] text-kult-muted block mb-2">{label}</span>
      <input {...props} data-testid={testId} className="w-full bg-transparent border border-kult-border px-3 py-2 font-mono text-sm focus:border-kult-pink outline-none" />
    </label>
  );
}
